import { useGetPreferences } from './UseGetPreferences';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AddPreferencePermissionRequest,
  AddPreferencesPermissionMutateOptions,
  PreferenceResponse,
  PreferenceResponseStatus,
} from '@utils/userPreferencesUtils/Types';
import { PreferenceApiService } from '@services/userPreferences/PreferencesApi.service';

export interface AddPreferencePermissionMutateResponse<T> {
  data: PreferenceResponse<T> | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  mutate: (variables: AddPreferencePermissionRequest, options?: AddPreferencesPermissionMutateOptions) => void;
  reset: () => void;
}

/**
 * @return AddPreferenceMutateResponse Object
 *
 * @remarks
 * Hooks for add preference permission api call using useMutate to manage server state
 *
 * @param type       - to difference the different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param namespace  - combination of retailer client and country
 * **/
export const useAddPermissionPreferences = <T>(
  type: string,
  namespace: string,
): AddPreferencePermissionMutateResponse<T> => {
  const getPreferences = useGetPreferences(type, namespace);

  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, isError, mutate, reset } = useMutation({
    mutationKey: ['preferences', type, namespace],

    mutationFn: async (request: AddPreferencePermissionRequest) => {
      const currentPreference = getPreferences.data.find(
        (preference) => preference.namespace === namespace && preference.key === request.key,
      );

      return PreferenceApiService.addPreferencesPermissions(
        currentPreference?.key ?? '',
        type,
        namespace,
        request.permissionBody,
      );
    },
    onSuccess: (response) => {
      if (response.status === PreferenceResponseStatus.SUCCESS) {
        queryClient.invalidateQueries({ queryKey: ['preferences'] });
      }
    },
  });

  return {
    data: data ?? null,
    isLoading: isPending,
    isError,
    isSuccess,
    mutate,
    reset,
  };
};
